import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Stack,
  Typography,
  OutlinedInput,
  MenuItem,
  FormControl,
  Select,
  Checkbox,
  Chip,
  Tooltip,
  InputLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/InfoOutlined';

import ErrorMessage from './ErrorMessage';
import { QuestionPhase } from '../constants';
import { getLabel } from '../pages/utils_v2/kumuHelper';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

export default function SelectActivity({
  categories,
  selectedActivities,
  setSelectedActivities,
  questionPhase,
  setQuestionPhase,
  setIsSubmit,
  isSubmit,
  setLegislation,
  setResults,
  setActivities
}) {
  const { t, i18n } = useTranslation('translation');
  const [showError, setShowError] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Wider menu on desktop, full width on mobile
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: isMobile ? '90vw' : 300,
      },
    },
    // On mobile, anchor the menu to fill width nicely
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    overflowY: 'scroll',
  };

  const handleChange = (event) => {
    const { target: { value } } = event;
    setSelectedActivities(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSubmit = () => {
    if (selectedActivities.length === 0) {
      setShowError(true);
    } else {
      setShowError(false);
      setIsSubmit(true);
    }
  };

  const handleBack = () => {
    setIsSubmit(false);
    setResults([])
    setLegislation([])
    setActivities([])
  };

  const renderLabel = (item) => getLabel(item, i18n.language) || '';

  return (
    <div>
      {showError && <ErrorMessage errorMessage={t("Please select activities")} />}

      <FormControl fullWidth>
        <InputLabel id="activity-select-label" sx={{ fontSize: isMobile ? 13 : 14 }}>
          {t("Please select categories")}
        </InputLabel>
        <Select
          labelId="activity-select-label"
          id="multiple-chip"
          multiple
          displayEmpty
          disabled={isSubmit}
          value={selectedActivities}
          onChange={handleChange}
          input={
            <OutlinedInput
              sx={{ fontSize: isMobile ? 13 : 14 }}
              label={t("Please select categories")}
            />
          }
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
              {selected.map((elm) => (
                <Chip
                  key={elm.id}
                  label={getLabel(elm, i18n.language) ?? null}
                  sx={{
                    borderRadius: '10px',
                    // Smaller chips on mobile so they don't overflow
                    height: isMobile ? 24 : 32,
                    '& .MuiChip-label': {
                      fontSize: isMobile ? 11 : 14,
                      px: isMobile ? 1 : 1.5,
                    },
                  }}
                />
              ))}
            </Box>
          )}
          MenuProps={MenuProps}
          sx={{
            // Taller touch target on mobile
            minHeight: isMobile ? 56 : 48,
            '& .MuiSelect-icon': {
              fontSize: 32,
            },
            '& legend': {
              fontSize: '1.05em',
            },
          }}
        >
          {categories
            .slice()
            .sort((a, b) => {
              const labelA = getLabel(a, i18n.language).toLowerCase();
              const labelB = getLabel(b, i18n.language).toLowerCase();
              return labelA.localeCompare(labelB);
            })
            .map((activity) => {
              const label = renderLabel(activity);
              const isSelected = selectedActivities.some((a) => a.id === activity.id);
              const definition =
                i18n.language === 'fr'
                  ? activity.value?.attributes?.["definition fr"]
                  : activity.value?.attributes?.definition;

              return (
                <MenuItem
                  key={activity.id}
                  value={activity}
                  sx={{
                    fontSize: isMobile ? 13 : 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    // Taller rows on mobile for easier tapping
                    minHeight: isMobile ? 52 : 48,
                    py: isMobile ? 1 : 0.75,
                    whiteSpace: 'normal', // allow long labels to wrap
                    wordBreak: 'break-word',
                  }}
                  
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, pr: 1 }}>
                    <Checkbox
                      checked={isSelected}
                      sx={{ p: isMobile ? '6px' : '9px' }}
                      size={'large'}
                    />
                    <Typography sx={{ fontSize: isMobile ? 13 : 14, lineHeight: 1.4 }}>
                      {label}
                    </Typography>
                  </Box>
                  {definition && (
                    <Tooltip
                      title={<span style={{ fontSize: '13px' }}>{definition}</span>}
                      arrow
                      enterTouchDelay={0}
                      leaveTouchDelay={5000}
                      // On mobile, show tooltip on tap instead of hover
                      disableHoverListener={isMobile}
                      disableFocusListener={false}
                      disableTouchListener={false}
                    >
                      <InfoIcon
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          fontSize: isMobile ? 28 : 24,
                          color: 'gray',
                          ml: 1,
                          flexShrink: 0,
                          // Larger tap target on mobile
                          p: isMobile ? '4px' : 0,
                        }}
                      />
                    </Tooltip>
                  )}
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>

      {questionPhase === QuestionPhase.ACTIVITYSELECT && (
        <Stack direction="row" spacing={2} mt={2}>
          {!isSubmit ? (
            <button
              className="btn btn-primary"
              style={{ width: isMobile ? '100%' : 'auto', minHeight: '44px' }}
              onClick={handleSubmit}
            >
              {t("Submit")}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              style={{ width: isMobile ? '100%' : 'auto', minHeight: '44px' }}
              onClick={handleBack}
            >
              {t("Back")}
            </button>
          )}
        </Stack>
      )}
    </div>
  );
}